import { isLoading, isSucceeded } from 'reporting-data/asyncData/AsyncData';
import { useAsyncDemographicData, useAsyncNavPersona } from '../../hooks/relevancyDataSourceHooks';
import { RELEVANCY_FACTORS } from '../../types/relevancyData';
import { getBookmarksRelevancyData, getHomepageRelevancyData } from './userDataSourceUtils/navPersonaUtils';
import { getLastPageRelevancyData } from './userDataSourceUtils/pageBasedRecUtils';
import { getRelevancyDataFromSurvey } from './userDataSourceUtils/userSurveyRecommendationUtils';
import { getRelevancyDataFromDerivedPersonas } from './userDataSourceUtils/userPersonaRecUtils';
const getIndexWeightedScore = (templateFactorData, relevancyFactorData) => {
  let score = 0;
  const contributingValues = [];
  templateFactorData.forEach((templateFactorValue, templateFactorValueIndex) => {
    const relevancyIndex = relevancyFactorData.indexOf(templateFactorValue);
    if (relevancyIndex > -1) {
      score += 1 - templateFactorValueIndex * 0.1 - relevancyIndex * 0.1;
      contributingValues.push(templateFactorValue);
    }
  });
  return {
    score,
    contributingValues
  };
};
const getRelevancyScoreForDataSource = (templateRecData, sourceRelevancyData, factorWeights) => {
  const factors = Object.keys(factorWeights);
  const contributingRelevancyData = {};
  let score = 0;
  factors.forEach(factor => {
    const templateFactorData = templateRecData[factor] || [];
    const relevancyFactorData = sourceRelevancyData[factor] || [];
    const {
      score: factorScore,
      contributingValues
    } = getIndexWeightedScore(templateFactorData, relevancyFactorData);
    if (factorScore > 0) {
      const factorWeight = factorWeights[factor];
      score += factorScore * (1 + factorWeight);
      contributingRelevancyData[factor] = contributingValues;
    }
  });
  return {
    score,
    contributingRelevancyData
  };
};
const getUniqueValuesFromRelevancyDataArrays = (arrays, valueKey) => Array.from(new Set(arrays.flatMap(data => data[valueKey] || [])));
export const getContributingRelevancyDataForTracking = contributingRelevancyData => {
  const dataSources = contributingRelevancyData.map(data => data.dataSource);
  const hubs = getUniqueValuesFromRelevancyDataArrays(contributingRelevancyData, 'hubs');
  const dataTypes = getUniqueValuesFromRelevancyDataArrays(contributingRelevancyData, 'dataTypes');
  const dimensionTypes = getUniqueValuesFromRelevancyDataArrays(contributingRelevancyData, 'dimensionTypes');
  return {
    source: dataSources,
    dataTypes,
    hub: hubs,
    dimensions: dimensionTypes
  };
};
export const getOverallRelevancyScore = (templateRecData, allDataSourceRelevancyData, factorWeights, dataSourceWeights) => {
  let score = 0;
  const contributingRelevancyDataWithSource = [];
  allDataSourceRelevancyData.forEach(sourceRelevancyData => {
    const {
      score: sourceRelevancyScore,
      contributingRelevancyData
    } = getRelevancyScoreForDataSource(templateRecData, sourceRelevancyData, factorWeights);
    if (sourceRelevancyScore > 0) {
      const {
        dataSource
      } = sourceRelevancyData;
      const sourceWeight = dataSourceWeights[dataSource];
      score += sourceRelevancyScore * (1 + sourceWeight);
      contributingRelevancyDataWithSource.push(Object.assign({
        dataSource
      }, contributingRelevancyData));
    }
  });
  const contributingRelevancyData = getContributingRelevancyDataForTracking(contributingRelevancyDataWithSource);
  return {
    score,
    contributingRelevancyData
  };
};
export const orderByFrequency = values => {
  const valueCounts = values.reduce((acc, value) => {
    acc[value] = (acc[value] || 0) + 1;
    return acc;
  }, {});
  return Object.keys(valueCounts).sort((a, b) => valueCounts[b] - valueCounts[a]);
};
export const combineRelevancyData = relevancyData => Object.values(RELEVANCY_FACTORS).reduce((combinedData, factor) => {
  const relevancyDataValues = relevancyData.reduce((acc, data) => {
    const factorValues = data[factor];
    if (factorValues) {
      acc.push(...factorValues);
    }
    return acc;
  }, []);
  const orderedValues = orderByFrequency(relevancyDataValues);
  combinedData[factor] = orderedValues;
  return combinedData;
}, {});
export const useRelevancyDataFromDataSources = userInfo => {
  const asyncNavPersona = useAsyncNavPersona();
  const asyncDemographicData = useAsyncDemographicData(userInfo.user.user_id);
  if (isLoading(asyncNavPersona) || isLoading(asyncDemographicData)) {
    return [];
  }
  const allRelevancyData = [];
  if (isSucceeded(asyncDemographicData)) {
    const userPersonaRelevancyData = getRelevancyDataFromDerivedPersonas(asyncDemographicData.data);
    if (userPersonaRelevancyData) {
      allRelevancyData.push(userPersonaRelevancyData);
    } else {
      // Fall back to survey data if assigned persona is unavailable or uninformative
      const surveyRelevancyData = getRelevancyDataFromSurvey(asyncDemographicData.data);
      if (surveyRelevancyData) {
        allRelevancyData.push(surveyRelevancyData);
      }
    }
  }
  const lastPageRelevancyData = getLastPageRelevancyData();
  if (lastPageRelevancyData) {
    allRelevancyData.push(lastPageRelevancyData);
  }
  if (isSucceeded(asyncNavPersona)) {
    const navPersona = asyncNavPersona.data;
    const homepageRelevancyData = getHomepageRelevancyData(navPersona);
    if (homepageRelevancyData) {
      allRelevancyData.push(homepageRelevancyData);
    }
    const bookmarksRelevancyData = getBookmarksRelevancyData(navPersona);
    if (bookmarksRelevancyData) {
      allRelevancyData.push(bookmarksRelevancyData);
    }
  }
  return allRelevancyData;
};
export const __TESTABLE__ = {
  getIndexWeightedScore,
  getRelevancyScoreForDataSource
};